import { useState, useEffect, useLayoutEffect, useMemo, useRef, useId } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import { PhoneInputWithDDI } from '../lib/PhoneInputWithDDI';
import {
  addAssociacao,
  getAssociacaoById,
  getAssociacoes,
  saveAssociacao,
  type Associacao,
} from '../lib/associacoesStorage';
import { getAgencias, getGuias } from '../lib/guiasAgenciasStorage';
import { getCidades, getCidadeById, type Cidade } from '../lib/experienciasStorage';
import { syncParceirosToApi } from '../lib/parceirosApi';
import { useRefetchWhenCatalogChanges } from '../contexts/AdminCatalogContext';

function montarEnderecoCompleto(
  cidade: Cidade,
  logradouro: string,
  numero: string,
  complemento: string,
  bairro: string
): string {
  const l1 = [logradouro.trim(), numero.trim()].filter(Boolean).join(', ');
  const lMid = [complemento.trim(), bairro.trim()].filter(Boolean).join(' — ');
  const cep = (cidade.cep || '').trim();
  const nomeC = (cidade.cidade || '').trim();
  const uf = (cidade.estadoProvincia || '').trim();
  const pais = (cidade.pais || '').trim();
  const l2 = [cep && `CEP ${cep}`, nomeC, uf, pais].filter(Boolean).join(' — ');
  return [l1, lMid, l2].filter((x) => x.length > 0).join(' | ');
}

const inputClass =
  'w-full py-2.5 px-3 rounded-lg border border-gray-300 text-sm text-gray-900 placeholder:text-gray-400 focus:ring-2 focus:ring-orange-500/30 focus:border-orange-500 outline-none transition-shadow';
const labelClass = 'block text-sm font-medium text-gray-700 mb-1';
const readOnlyClass = `${inputClass} bg-gray-100 text-gray-700 cursor-default border-gray-200`;

export default function AssociacaoFormPage() {
  const { associacaoId } = useParams<{ associacaoId: string }>();
  const isEdit = Boolean(associacaoId);
  const navigate = useNavigate();

  const [cidadesTick, setCidadesTick] = useState(0);
  const [cidadeId, setCidadeId] = useState('');
  const [cidadeQuery, setCidadeQuery] = useState('');
  const [cidadeListOpen, setCidadeListOpen] = useState(false);
  const [logradouro, setLogradouro] = useState('');
  const [numero, setNumero] = useState('');
  const [complemento, setComplemento] = useState('');
  const [bairro, setBairro] = useState('');
  const [razaoSocial, setRazaoSocial] = useState('');
  const [email, setEmail] = useState('');
  const [telefone, setTelefone] = useState('');
  const [cnpj, setCnpj] = useState('');
  /** Nome estável e neutro: evita heurística de morada do Chrome em `name`/`id` com "cidade"/"address". */
  const [cidadeBuscaInputName] = useState(() => `nvxq_${Math.random().toString(36).slice(2, 11)}`);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [editingRef, setEditingRef] = useState<Associacao | null>(null);
  const cidadeFiltroDomId = useId();
  const cidadeWrapRef = useRef<HTMLDivElement>(null);

  useRefetchWhenCatalogChanges(() => setCidadesTick((n) => n + 1));

  const cidades = useMemo(() => {
    void cidadesTick;
    return getCidades()
      .slice()
      .sort((a, b) =>
        (a.cidade || '').localeCompare(b.cidade || '', 'pt-BR', { sensitivity: 'base' })
      );
  }, [cidadesTick]);

  const cidadeSel = cidadeId ? getCidadeById(cidadeId) : null;

  const cidadesFiltradas = useMemo(() => {
    const q = cidadeQuery.trim().toLowerCase();
    if (!q) return cidades.slice(0, 60);
    return cidades
      .filter(
        (c) =>
          (c.cidade || '').toLowerCase().includes(q) ||
          (c.estadoProvincia || '').toLowerCase().includes(q) ||
          (c.pais || '').toLowerCase().includes(q)
      )
      .slice(0, 80);
  }, [cidades, cidadeQuery]);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (!cidadeWrapRef.current?.contains(e.target as Node)) setCidadeListOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  useLayoutEffect(() => {
    if (!isEdit || !associacaoId) {
      setLoadError(null);
      setEditingRef(null);
      return;
    }
    const a = getAssociacaoById(associacaoId);
    if (!a) {
      setLoadError('Associação não encontrada.');
      setEditingRef(null);
      return;
    }
    setLoadError(null);
    setEditingRef(a);
    setRazaoSocial(a.razaoSocial);
    setEmail(a.email);
    setTelefone(a.telefone);
    setCnpj(a.cnpj);
    setCidadeId(a.cidadeId);
    const c = getCidadeById(a.cidadeId);
    setCidadeQuery(c ? `${c.cidade} — ${c.estadoProvincia}` : '');
    setLogradouro(a.enderecoLogradouro);
    setNumero(a.enderecoNumero);
    setComplemento(a.enderecoComplemento ?? '');
    setBairro(a.enderecoBairro ?? '');
  }, [isEdit, associacaoId]);

  const limparCidade = () => {
    setCidadeId('');
    setCidadeQuery('');
    setLogradouro('');
    setNumero('');
    setComplemento('');
    setBairro('');
    setCidadeListOpen(false);
  };

  const escolherCidade = (c: Cidade) => {
    setCidadeId(c.id);
    setCidadeQuery(`${c.cidade} — ${c.estadoProvincia}`);
    setCidadeListOpen(false);
  };

  const handleSalvar = () => {
    if (!cidadeSel) {
      alert('Escolha uma cidade cadastrada no sistema.');
      return;
    }
    if (!logradouro.trim()) {
      alert('Preencha o logradouro (rua/avenida).');
      return;
    }
    if (!numero.trim()) {
      alert('Preencha o número.');
      return;
    }
    if (!razaoSocial.trim()) {
      alert('Nome da associação / Razão Social é obrigatório.');
      return;
    }
    if (!email.trim()) {
      alert('E-mail da associação é obrigatório.');
      return;
    }
    if (!telefone.trim()) {
      alert('Telefone/WhatsApp é obrigatório.');
      return;
    }
    if (!cnpj.trim()) {
      alert('CNPJ é obrigatório.');
      return;
    }

    const enderecoCompleto = montarEnderecoCompleto(
      cidadeSel,
      logradouro,
      numero,
      complemento,
      bairro
    );

    if (isEdit && editingRef) {
      saveAssociacao({
        ...editingRef,
        razaoSocial: razaoSocial.trim(),
        email: email.trim(),
        enderecoCompleto,
        cidadeId: cidadeSel.id,
        enderecoLogradouro: logradouro.trim(),
        enderecoNumero: numero.trim(),
        enderecoComplemento: complemento.trim() || undefined,
        enderecoBairro: bairro.trim() || undefined,
        telefone: telefone.trim(),
        cnpj: cnpj.trim(),
      });
      void syncParceirosToApi(getGuias(), getAgencias(), getAssociacoes());
      navigate('/associacao');
      return;
    }

    addAssociacao({
      razaoSocial: razaoSocial.trim(),
      email: email.trim(),
      enderecoCompleto,
      cidadeId: cidadeSel.id,
      enderecoLogradouro: logradouro.trim(),
      enderecoNumero: numero.trim(),
      enderecoComplemento: complemento.trim() || undefined,
      enderecoBairro: bairro.trim() || undefined,
      telefone: telefone.trim(),
      cnpj: cnpj.trim(),
      status: 'pendente',
      guiaIds: [],
    });

    limparCidade();
    setRazaoSocial('');
    setEmail('');
    setTelefone('');
    setCnpj('');
    void syncParceirosToApi(getGuias(), getAgencias(), getAssociacoes());
    navigate('/associacao');
  };

  const temCidades = cidades.length > 0;
  const podePreencher = !!cidadeId && !!cidadeSel;

  if (isEdit && loadError) {
    return (
      <AdminLayout>
        <div className="w-full min-w-0 px-4 sm:px-6 lg:px-8 py-6">
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
            {loadError}{' '}
            <Link to="/associacao" className="font-semibold text-orange-700 hover:underline">
              Voltar às associações
            </Link>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="w-full min-w-0 px-4 sm:px-6 lg:px-8 py-6">
        <nav className="text-sm text-gray-500 mb-2">
          <Link to="/dashboard" className="hover:text-gray-700">
            Home
          </Link>
          {' > '}
          <Link to="/associacao" className="hover:text-gray-700">
            Associações
          </Link>
          {' > '}
          <span className="text-gray-800">{isEdit ? 'Editar' : 'Nova'}</span>
        </nav>

        <div className="mb-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight">
              {isEdit ? 'Editar associação' : 'Criar associação'}
            </h1>
            <p className="text-sm text-gray-600 mt-1 max-w-3xl">
              A associação fica vinculada a uma{' '}
              <strong>cidade já cadastrada</strong> em{' '}
              <Link to="/cidades" className="text-orange-600 font-medium hover:underline">
                Cidades
              </Link>
              . Escolha a cidade e complete o endereço da sede.
            </p>
          </div>
          <Link
            to="/associacao"
            className="inline-flex items-center justify-center px-4 py-2.5 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 shrink-0"
          >
            Voltar à lista
          </Link>
        </div>

        {!temCidades && (
          <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
            Não há cidades no catálogo. Cadastre cidades em{' '}
            <Link to="/cidades" className="font-semibold text-orange-700 hover:underline">
              Cidades
            </Link>{' '}
            antes de criar uma associação.
          </div>
        )}

        <form
          className="w-full max-w-none space-y-6"
          autoComplete="off"
          onSubmit={(e) => e.preventDefault()}
        >
          <fieldset className="w-full min-w-0 rounded-xl border border-gray-200 bg-white shadow-sm p-5 sm:p-6">
            <legend className="sr-only">Dados da associação</legend>
            <h2 className="text-lg font-semibold text-gray-900 mb-1">Dados da associação</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>Nome associação / Razão Social *</label>
                <input
                  type="text"
                  value={razaoSocial}
                  onChange={(e) => setRazaoSocial(e.target.value)}
                  className={inputClass}
                  placeholder="Nome da associação"
                />
              </div>
              <div>
                <label className={labelClass}>E-mail *</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={inputClass}
                  placeholder="email@associacao.com"
                />
              </div>
              <div>
                <label className={labelClass}>Telefone (WhatsApp) *</label>
                <PhoneInputWithDDI
                  value={telefone}
                  onChange={setTelefone}
                  placeholder="(11) 99999-9999"
                  className="w-full"
                />
              </div>
              <div>
                <label className={labelClass}>CNPJ *</label>
                <input
                  type="text"
                  value={cnpj}
                  onChange={(e) => setCnpj(e.target.value)}
                  className={inputClass}
                  placeholder="00.000.000/0000-00"
                />
              </div>
            </div>
          </fieldset>

          <section className="w-full rounded-xl border border-gray-200 bg-white shadow-sm p-5 sm:p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-1">Cidade da associação *</h2>
            <p className="text-xs text-gray-500 mb-4">
              Pesquise pelo nome da cidade, estado ou país. Os dados postais abaixo são preenchidos automaticamente e
              não podem ser editados.
            </p>
            <div ref={cidadeWrapRef} className="relative max-w-2xl">
              <label className={labelClass} htmlFor={cidadeFiltroDomId}>
                Buscar cidade
              </label>
              <input
                id={cidadeFiltroDomId}
                name={cidadeBuscaInputName}
                type="text"
                inputMode="text"
                enterKeyHint="go"
                value={cidadeQuery}
                disabled={!temCidades}
                onChange={(e) => {
                  const v = e.target.value;
                  setCidadeQuery(v);
                  setCidadeListOpen(true);
                  if (!v.trim()) {
                    setCidadeId('');
                    return;
                  }
                  if (cidadeSel) {
                    const label = `${cidadeSel.cidade} — ${cidadeSel.estadoProvincia}`;
                    if (v !== label) setCidadeId('');
                  }
                }}
                onFocus={() => {
                  if (temCidades) setCidadeListOpen(true);
                }}
                placeholder={temCidades ? 'Filtrar no catálogo…' : 'Cadastre cidades primeiro'}
                className={inputClass}
                autoComplete="section-navix nvx-catalog-filter-not-address"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
                data-lpignore="true"
                data-1p-ignore
                data-form-type="other"
                data-nvx-internal-catalog-search="true"
                role="combobox"
                aria-autocomplete="list"
                aria-expanded={cidadeListOpen}
                aria-controls={cidadeListOpen ? `${cidadeFiltroDomId}-listbox` : undefined}
              />
              {cidadeListOpen && temCidades && cidadesFiltradas.length > 0 && (
                <ul
                  id={`${cidadeFiltroDomId}-listbox`}
                  className="absolute z-50 mt-1 w-full max-h-60 overflow-auto rounded-lg border border-gray-200 bg-white py-1 shadow-lg"
                  role="listbox"
                >
                  {cidadesFiltradas.map((c) => (
                    <li key={c.id}>
                      <button
                        type="button"
                        role="option"
                        className="w-full px-3 py-2.5 text-left text-sm text-gray-800 hover:bg-orange-50"
                        onClick={() => escolherCidade(c)}
                      >
                        <span className="font-medium">{c.cidade}</span>
                        <span className="text-gray-500"> — {c.estadoProvincia}</span>
                        {c.pais ? <span className="text-gray-400"> · {c.pais}</span> : null}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {cidadeId && (
                <button
                  type="button"
                  onClick={limparCidade}
                  className="mt-2 text-sm font-medium text-orange-600 hover:text-orange-800"
                >
                  Alterar cidade
                </button>
              )}
            </div>
          </section>

          {cidadeSel && (
            <section className="w-full rounded-xl border border-gray-200 bg-gray-50/80 p-5 sm:p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Dados da cidade (somente leitura)</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label className={labelClass}>CEP</label>
                  <input readOnly value={cidadeSel.cep || '—'} className={readOnlyClass} tabIndex={-1} />
                </div>
                <div>
                  <label className={labelClass}>Estado / província</label>
                  <input readOnly value={cidadeSel.estadoProvincia || '—'} className={readOnlyClass} tabIndex={-1} />
                </div>
                <div>
                  <label className={labelClass}>País</label>
                  <input readOnly value={cidadeSel.pais || '—'} className={readOnlyClass} tabIndex={-1} />
                </div>
                <div>
                  <label className={labelClass}>Cidade (cadastro)</label>
                  <input readOnly value={cidadeSel.cidade || '—'} className={readOnlyClass} tabIndex={-1} />
                </div>
              </div>
            </section>
          )}

          <fieldset
            disabled={!podePreencher}
            className="w-full min-w-0 rounded-xl border border-gray-200 bg-white shadow-sm p-5 sm:p-6 space-y-6 disabled:opacity-55 disabled:pointer-events-none"
          >
            <legend className="sr-only">Endereço da sede</legend>

            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-1">Endereço da sede</h2>
              <p className="text-xs text-gray-500 mb-4">Complete o endereço na cidade selecionada.</p>
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className={labelClass}>Logradouro *</label>
                  <input
                    type="text"
                    value={logradouro}
                    onChange={(e) => setLogradouro(e.target.value)}
                    className={inputClass}
                    placeholder="Rua, avenida, travessa…"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div>
                    <label className={labelClass}>Número *</label>
                    <input
                      type="text"
                      value={numero}
                      onChange={(e) => setNumero(e.target.value)}
                      className={inputClass}
                      placeholder="Nº"
                    />
                  </div>
                  <div className="sm:col-span-1 lg:col-span-2">
                    <label className={labelClass}>Complemento</label>
                    <input
                      type="text"
                      value={complemento}
                      onChange={(e) => setComplemento(e.target.value)}
                      className={inputClass}
                      placeholder="Sala, bloco, andar…"
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Bairro</label>
                    <input
                      type="text"
                      value={bairro}
                      onChange={(e) => setBairro(e.target.value)}
                      className={inputClass}
                      placeholder="Bairro"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-gray-100">
              <button
                type="button"
                onClick={handleSalvar}
                className="px-5 py-2.5 bg-orange-500 text-white rounded-lg text-sm font-semibold hover:bg-orange-600 shadow-sm disabled:opacity-50"
              >
                {isEdit ? 'Guardar alterações' : 'Salvar associação'}
              </button>
              {!podePreencher && temCidades && (
                <span className="text-sm text-gray-500">Selecione uma cidade para desbloquear o formulário.</span>
              )}
            </div>
          </fieldset>
        </form>
      </div>
    </AdminLayout>
  );
}
